import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import { Box, Chip, CircularProgress, Grid, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';

import MasterComponent from '../../common/MasterComponent';
import SelectComponent from '../../components/Select';
import { pageActions } from '../../store/reducers/page-actions';
import { userManagementActions } from '../../store/reducers/user-management';
import { userRoleActions } from '../../store/reducers/user-role';

import {
  CONNECTIVITY_DASHBOARD_API_PATHS,
  DEFAULT_FEATURE_TITLES,
  DEFAULT_ROLES_ACCESS,
  DIGITAL_TWIN_API_PATHS,
  getFeatureLabel
} from '../../utils/user-management';

import { ReactComponent as CheckIcon } from '../../assets/icons/main/check-icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/main/close-icon.svg';
import { palette } from '../../theme/palette';
interface UserAccessListType {
  isUserAccessListLoading: boolean;
  userAccessList: any;
  userRole: Array<any>;
  isUpdateUserSuccess: boolean;
  ontologyData: Array<any>;
  loggedInUserAccess: Array<any>;
  settingsData: any
}

const TENANT_SETTINGS_ATTR = 'tenant/settings';

function UserAccessControl({
  userAccessList,
  isUserAccessListLoading,
  userRole,
  isUpdateUserSuccess,
  ontologyData,
  loggedInUserAccess,
  settingsData
}: UserAccessListType) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [userRoleList, setUserRoleList] = useState(null);
  const [rows, setRows] = useState<any>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const { user_id }: any = useParams();
  const tabScreen = useMediaQuery('(max-width:768px)');
  const mdScreen = useMediaQuery('(max-width:1024px)');
  const outletContext = useOutletContext();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(userManagementActions.getUserAccessList(user_id));
    dispatch(userRoleActions.getRoleList());

    return () => {
      dispatch(userManagementActions.resetUserAccessList());
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(userAccessList) && !userAccessList.is_allowed) {
      navigate('/user-management');
    }
  }, [JSON.stringify(userAccessList)]);

  useEffect(() => {
    if (isUpdateUserSuccess) {
      dispatch(userManagementActions.getUserAccessList(user_id));
    }
  }, [isUpdateUserSuccess]);

  const filteredRoles: any = userRole?.filter(
    (role: any) => !['default-roles-iot_in_box', 'offline_access', 'uma_authorization'].includes(role.name)
  );
  const isUserEnabled = userAccessList.enabled;

  const handleRoleChange = (e: any) => {
    const selectedId = e?.target.value;
    const roleObject = filteredRoles.find((item: any) => item?.id === selectedId);
    setSelectedRole(selectedId);
    setUserRoleList(roleObject);
  };

  const handleSave = () => {
    const reqParams: any = {
      id: userAccessList.id,
      email: userAccessList.email,
      username: userAccessList.email,
      roles: [userRoleList],
    };
    dispatch(userManagementActions.updateUserRequest(reqParams));
  };

  const decodeValue = (value: any) => {
    const decodedValue = parseInt(value, 10);
    return {
      read: (decodedValue & 1) === 1,
      create: (decodedValue & 4) === 4,
      update: (decodedValue & 2) === 2,
      delete: (decodedValue & 8) === 8,
      approver: (decodedValue & 16) === 16,
    };
  };

  useEffect(() => {
    const filteredList = {
      ...userAccessList,
      roles: userAccessList?.roles?.filter((role: any) => role.name !== 'default-roles-iot_in_box'),
    };
    if (filteredList && filteredList.roles) {
      const role: any = filteredList?.roles?.map((role: any) => role.id);
      setSelectedRole(role);

      if (Object.keys(filteredList.roles[0].attributes).length === 0) {
        setRows(
          DEFAULT_ROLES_ACCESS.map((item: any) => ({
            ...item,
            feature: getFeatureLabel(settingsData, item),
          }))
        );
      } else {
        // console.log('filteredList.roles[0]',filteredList.roles[0].attributes)
        // const initialRows: any = Object.entries(filteredList.roles[0].attributes).map(
        //   ([object_name, value]: any, index: any) => ({
        //     id: index + 1,
        //     object_name,
        //     read: decodeValue(value).read,
        //     create: decodeValue(value).create,
        //     update: decodeValue(value).update,
        //     delete: decodeValue(value).delete,
        //     approver: decodeValue(value).approver,
        //   })
        // );
        const roleAttributes: any = filteredList.roles[0].attributes;
        setRows(
          DEFAULT_ROLES_ACCESS.map((item: any, index: number) => {
            if (item.object_name === 'things/connectivity_dashboard') {
              return {
                ...item,
                feature: DEFAULT_FEATURE_TITLES[item.object_name.trim()],
                all: roleAttributes[item.object_name.trim()]
                  ? roleAttributes[item.object_name.trim()][0] === '31'
                  : false,
                read: CONNECTIVITY_DASHBOARD_API_PATHS.every(
                  (path: any) => get(roleAttributes, `${path}[0]`, '0') === '1'
                ),
                create: false,
                update: false,
                delete: false,
                approver: false,
              };
            }
            if (item.object_name === 'digital_twin/twin') {
              return {
                ...item,
                feature: DEFAULT_FEATURE_TITLES[item.object_name.trim()],
                all: roleAttributes[item.object_name.trim()]
                  ? roleAttributes[item.object_name.trim()][0] === '31'
                  : false,
                read: DIGITAL_TWIN_API_PATHS.every((path: any) => get(roleAttributes, `${path}[0]`, '0') === '1'),
                create: DIGITAL_TWIN_API_PATHS.every((path: any) => get(roleAttributes, `${path}[0]`, '0') === '2'),
                update: DIGITAL_TWIN_API_PATHS.every((path: any) => get(roleAttributes, `${path}[0]`, '0') === '4'),
                delete: DIGITAL_TWIN_API_PATHS.every((path: any) => get(roleAttributes, `${path}[0]`, '0') === '8'),
                approver: false,
              };
            }
            return {
              ...item,
              feature: DEFAULT_FEATURE_TITLES[item.object_name.trim()],
              all: roleAttributes[item.object_name.trim()]
                ? roleAttributes[item.object_name.trim()][0] === '31'
                : false,
              read: decodeValue(roleAttributes[item.object_name.trim()]).read,
              create: decodeValue(roleAttributes[item.object_name.trim()]).create,
              update: decodeValue(roleAttributes[item.object_name.trim()]).update,
              delete: decodeValue(roleAttributes[item.object_name.trim()]).delete,
              approver: decodeValue(roleAttributes[item.object_name.trim()]).approver,
            };
          })
        );
        // setRows(initialRows);
      }
    }
  }, [JSON.stringify(userAccessList)]);

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/');
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index > 0 && index !== 2)
          .map((item: any, index: number) => {
            if (index == 0) {
              return {
                route: pathnamesTemp[0] + '/' + pathnamesTemp[1],
                pathName: item.replace('-', ' '),
                navigate: true,
              };
            } else if (index == 1) {
              return { route: '', pathName: item.replace(/-/g, ' '), navigate: false };
            } else {
              return { route: '', pathName: item.replace('-', ' '), navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [JSON.stringify(rows), pathname]);

  const renderStatus = (params: any) => {
    const status = params.value;

    return status ? <CheckIcon style={{ color: palette.success.dark }} /> : <CloseIcon style={{ color: palette.error.main }} />;
  };

  const columns = [
    {
      field: 'feature',
      headerName: 'Features',
      flex: 1,
      minWidth: 380,
      maxWidth: 380,
      renderCell: (params: any) => {
        const featureName =
          params.value !== 'Shop Floor Configuration'
            ? params.value
            : (ontologyData?.slice(-1)?.[0]?.level_name ?? 'Shop floor') + ' Configuration';

        return (
          <Tooltip title={featureName} placement='left' arrow>
            <Typography
              component='p'
              variant='subtitle1'
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {featureName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'read',
      headerName: 'READ',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: renderStatus,
    },
    {
      field: 'create',
      headerName: 'CREATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: renderStatus,
    },
    {
      field: 'update',
      headerName: 'UPDATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: renderStatus,
    },
    {
      field: 'delete',
      headerName: 'DELETE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: renderStatus,
    },
    {
      field: 'approver',
      headerName: 'APPROVE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: renderStatus,
    },
  ];

  const partLabel = (
    <div>
      <Grid container direction='row' justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Grid
            container
            direction='row'
            justifyContent='flex-start'
            gap={tabScreen || mdScreen ? '10px' : '32px'}
            alignItems='center'
          >
            <Grid item>
              <Typography variant='subtitle1'>
                Name : <b>{`${userAccessList?.firstName || ''} ${userAccessList?.lastName || ''}`}</b>{' '}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='subtitle1'>
                Email Address : <b>{userAccessList?.email || ''}</b>{' '}
              </Typography>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='subtitle1' sx={{ paddingRight: '4px' }}>
                  Role:
                </Typography>
                <SelectComponent
                  value={selectedRole}
                  onChange={handleRoleChange}
                  options={filteredRoles.map((option: any) => ({ ...option, label: option.name, value: option.id }))}
                  name='role'
                  width={250}
                />
              </Box>
            </Grid>
            <Grid item>
              <Typography variant='subtitle1'>
                Status :{' '}
                <Chip
                  label={isUserEnabled ? 'Enabled' : 'Disabled'}
                  sx={
                    isUserEnabled
                      ? { backgroundColor: palette.success.dark, color: palette.primary.contrastText }
                      : { border: `1px solid ${palette.secondary.dark}`, color: palette.secondary.dark, backgroundColor: palette.primary.contrastText }
                  }
                />{' '}
              </Typography>
            </Grid>
          </Grid>
          {/* )} */}
        </Grid>
      </Grid>
    </div>
  );

  return (
    <>
      <MasterComponent
        heading={isUserAccessListLoading && !isUpdateUserSuccess ? <CircularProgress size={30} /> : partLabel}
        showSaveButton
        handleSave={handleSave}
        isLoading={isUserAccessListLoading}
        rows={rows.filter((row: any) => {
          if (row.object_name === TENANT_SETTINGS_ATTR) {
            const loggedUserTenantSettings: any = get(loggedInUserAccess, TENANT_SETTINGS_ATTR, {});
            return (
              loggedUserTenantSettings.read &&
              loggedUserTenantSettings.create &&
              loggedUserTenantSettings.delete &&
              loggedUserTenantSettings.update
            );
          }
          return true;
        })}
        columns={columns}
        pageSize={17}
        hideFooter
        isFromRoleAccess={true}
        // setIsModalOpen={setIsModalOpen}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            data not available
          </Stack>
        )}
      />
    </>
  );
}

const mapStateToProps = (state: any) => ({
  isUserAccessListLoading: state.userManagement.isUserAccessListLoading,
  userAccessList: state.userManagement.userAccessList,
  userRole: state.userRole.userRole.filter((role: any) => role.is_allowed),
  isUpdateUserSuccess: state.userManagement.isUpdateUserSuccess,
  ontologyData: state.ontologyMaster.ontologyData,
  loggedInUserAccess: state.profile.userAccess,
  settingsData: state.settings.settingsData,
});

export default connect(mapStateToProps)(UserAccessControl);
